import type { ToolUIPart } from "ai"
import { AlertCircle, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { useCoachThreadApi } from "@/lib/coach-thread-api"
import type { PruefungsstandKarte } from "@nl/shared"

/**
 * Wie weit die Beraterin ist, als Karte im Gespräch.
 *
 * Seit die Lernfeld-Auswahl aufgelöst ist, gibt es keine Seite mehr, die den
 * Fortschritt trägt. Er kommt jetzt dorthin, wo geübt wird — der Coach legt die
 * Karte zu Beginn und zwischendurch hin, statt Zahlen vorzulesen.
 *
 * Der Merkzettel steht bewusst nicht darauf: er ist in Prosa über sie
 * formuliert und läse sich als Aufzählung wie ein Mängelbericht.
 */
export function PruefungsstandCard({ part }: { part: ToolUIPart }) {
  if (part.state === "input-streaming" || part.state === "input-available") {
    return (
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Dein Stand wird geholt …
      </div>
    )
  }

  if (part.state === "output-error") {
    return (
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <AlertCircle className="h-3.5 w-3.5" />
        Dein Stand konnte nicht geladen werden.
      </div>
    )
  }

  const stand = (part.output as { karte?: PruefungsstandKarte } | undefined)
    ?.karte
  if (!stand) return null

  return <Stand stand={stand} />
}

function Stand({ stand }: { stand: PruefungsstandKarte }) {
  const thread = useCoachThreadApi()
  const gesehen = stand.lernfelder.reduce((n, l) => n + l.gesehen, 0)
  const gesamt = stand.lernfelder.reduce((n, l) => n + l.gesamt, 0)
  const sitzen = stand.lernfelder.reduce((n, l) => n + l.sitzen, 0)
  const quote = stand.versuche
    ? Math.round((stand.richtige / stand.versuche) * 100)
    : null

  // Das schwächste Lernfeld mit Erfahrung: ohne Versuche ist eine Quote keine
  // Aussage, und „vertiefe dein schwächstes Thema“ wäre dann geraten.
  const schwaechstes = [...stand.lernfelder]
    .filter((l) => l.quote !== null)
    .sort((a, b) => (a.quote ?? 0) - (b.quote ?? 0))[0]

  return (
    <div className="rounded-xl border border-[var(--rule)] bg-card p-4">
      <div className="eyebrow text-muted-foreground">Dein Stand</div>

      {stand.versuche === 0 ? (
        <p className="mt-1.5 text-[14px] leading-relaxed">
          Noch nichts beantwortet. {gesamt} Fragen warten auf dich.
        </p>
      ) : (
        <p className="mt-1.5 text-[14px] leading-relaxed">
          <span className="tnum font-medium">
            {gesehen} von {gesamt}
          </span>{" "}
          Fragen gesehen
          {quote !== null && (
            <>
              {" "}
              · <span className="tnum font-medium">{quote} %</span> richtig
            </>
          )}
          .
        </p>
      )}
      {stand.versuche > 0 && (
        <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
          Sicher gelernt:{" "}
          <span className="tnum font-medium text-foreground">{sitzen}</span>{" "}
          {sitzen === 1 ? "Frage" : "Fragen"}. Als sicher gilt eine Frage, wenn
          du sie viermal hintereinander richtig beantwortet hast, mit ein paar
          Tagen Abstand.
        </p>
      )}

      {/* Haarlinien statt Abstand: ein leerer Fortschrittsbalken sieht selbst
          wie eine Trennlinie aus, und ohne echte Linien gruppierte das Auge
          Balken und nächste Zeile zusammen. Jetzt umschließt je eine Linie
          eine Zeile, und der Balken liegt sichtbar darin. */}
      <div className="mt-3 divide-y divide-[var(--rule)] border-y border-[var(--rule)]">
        {stand.lernfelder.map((lernfeld) => (
          <div key={lernfeld.slug} className="py-2.5">
            <div className="flex items-baseline justify-between gap-3 text-[13px]">
              <span className="min-w-0 truncate">{lernfeld.titel}</span>
              <span className="tnum shrink-0 text-muted-foreground">
                {lernfeld.gesehen}/{lernfeld.gesamt}
                {lernfeld.quote !== null && ` · ${lernfeld.quote} %`}
              </span>
            </div>
            <Progress
              value={
                lernfeld.gesamt
                  ? Math.round((lernfeld.gesehen / lernfeld.gesamt) * 100)
                  : 0
              }
              className="mt-2 h-1"
            />
          </div>
        ))}
      </div>

      {stand.pruefungAm && (
        <p className="mt-2.5 text-[12.5px] text-muted-foreground">
          Deine Prüfung ist am {stand.pruefungAm}.
        </p>
      )}

      {/* Die Karte ist kein Bericht, sondern ein Abzweig: von hier aus geht es
          weiter, ohne dass sie den Satz selbst tippen muss. */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() =>
            thread?.ask("Frag mich weiter, gern gemischt über alle Lernfelder.")
          }
        >
          Weiter gemischt
        </Button>
        {schwaechstes && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              thread?.ask(`Frag mich gezielt zu „${schwaechstes.titel}“ ab.`)
            }
          >
            {schwaechstes.titel} vertiefen
          </Button>
        )}
      </div>
    </div>
  )
}
